import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Zap, Crown, ArrowRight, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Barra discreta no topo da busca para usuários logados FREE.
 * Mostra "X / 3 consultas grátis" com barra de progresso.
 * Anônimo: mostra prompt de login. PRO: não renderiza nada.
 * Expose via prop `refreshKey` para atualizar após cada análise.
 */
const FreeQuotaBanner = ({ refreshKey = 0 }) => {
  const { user, token, isPro } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    if (!user || !token) { setUsage(null); return; }
    try {
      const resp = await axios.get(`${API}/api/plans/usage`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        timeout: 8000,
      });
      setUsage(resp.data);
    } catch { /* silent */ }
  }, [user, token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Anonymous: gentle nudge to create account
  if (!user) {
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#F0F6FF] border border-[#BFDBFE] rounded-md text-xs"
        data-testid="free-quota-banner-anon"
      >
        <div className="flex items-center gap-2 text-[#1E3A5F]">
          <Lock className="w-4 h-4 shrink-0" />
          <span>Crie sua conta grátis para liberar até 3 consultas de candidatos.</span>
        </div>
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-1 text-[#1E3A5F] font-bold hover:underline shrink-0"
          data-testid="free-quota-banner-login"
        >
          Entrar / Criar conta <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // PRO: nothing — they already have unlimited access.
  if (isPro) return null;

  // Loading state (first render before /usage returns)
  if (!usage) return null;

  const used = usage.consultas_count ?? 0;
  const limit = usage.consultas_limite ?? 3;
  const remaining = Math.max(0, usage.consultas_restantes ?? (limit - used));
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const exhausted = remaining <= 0;

  const barColor = exhausted
    ? 'bg-[#C62828]'
    : remaining === 1
      ? 'bg-[#F57F17]'
      : 'bg-[#006030]';

  const textColor = exhausted
    ? 'text-[#C62828]'
    : remaining === 1
      ? 'text-[#F57F17]'
      : 'text-[#006030]';

  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-md ${exhausted ? 'border-[#C62828]/30 bg-[#FFF5F5]' : 'border-[#E2E8F0]'}`}
      data-testid="free-quota-banner"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Zap className={`w-4 h-4 shrink-0 ${textColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-xs font-bold text-[#0F1C2E]" data-testid="free-quota-count">
              {used}/{limit} consultas grátis usadas
            </span>
            <span className={`text-[10px] font-medium ${textColor} hidden sm:inline`}>
              {exhausted
                ? 'Limite atingido'
                : remaining === 1
                  ? 'Última consulta grátis!'
                  : `${remaining} restante${remaining === 1 ? '' : 's'}`}
            </span>
          </div>
          <div className="h-1.5 w-full bg-[#F4F6F8] rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor} transition-all duration-500`}
              style={{ width: `${pct}%` }}
              data-testid="free-quota-bar"
            />
          </div>
        </div>
      </div>
      <button
        onClick={() => navigate('/planos')}
        className={`flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-md shrink-0 transition-colors ${
          exhausted
            ? 'bg-[#C62828] text-white hover:bg-[#8B0000]'
            : 'bg-[#006030] text-white hover:bg-[#004020]'
        }`}
        data-testid="free-quota-cta"
      >
        <Crown className="w-3 h-3" />
        {exhausted ? 'Liberar por R$ 14,90' : 'Ver Plano Anual'}
      </button>
    </div>
  );
};

export default FreeQuotaBanner;
